import ky from 'ky';
import { RootStateOrAny, useSelector } from 'react-redux';
import queryString from 'query-string';
import { FullStory } from '@fullstory/browser';
import useCommonRequestOptions from '../useCommonRequestOptions';
import {
  AuthResponse,
  VerifySocialAuthPayload,
  VerifyEmailPayload,
  VerifyPhoneNumberSend2FAPayload,
  VerifyTwoFAPayload,
  TwilioVerifySend2FAPayload,
  VerifyOneTapUserPayload,
  VerifyOneTapUserResponse,
  NewUser2FAVerifyRes,
  VerifyTwoFAPinResetPayload,
} from '../../library/interfaces';
import { isMobileOrTabletBrowser } from '../../utils';
import { useHistory } from 'react-router-dom';

const { FLUZ_UNIFIED_WHITELIST = '' } = process.env;
const TRUSTED_ORIGINS = process.env.NODE_ENV === 'development' ? ['*'] : FLUZ_UNIFIED_WHITELIST.split(',');

export default function useAuth() {
  const history = useHistory();
  const { redirectFrom, isIframe } = useSelector((state: RootStateOrAny) => state.auth);
  const { email, firstName, lastName, userId } = useSelector((state: RootStateOrAny) => state.user);
  const { getCommonRequestOptions } = useCommonRequestOptions();

  async function verifySocialAuth(payload: VerifySocialAuthPayload): Promise<AuthResponse> {
    return ky
      .post('social-oauth/verify', { ...getCommonRequestOptions('auth', { useBasicAuth: true }), json: payload })
      .json();
  }

  async function verifyEmailSend2FA(payload: VerifyEmailPayload): Promise<
    | {
        isNewUser: false;
        phoneNumber: string;
        sentSMS: boolean;
        token: string;
        hasPowerTag: boolean;
        firstName: string;
        lastName: string;
        userId: string;
        isTwilioVerify: boolean;
      }
    | { isNewUser: true; phoneNumber: string; sentSMS: boolean; isTwilioVerify: boolean }
  > {
    return ky
      .post('email/verify', { ...getCommonRequestOptions('auth', { useBasicAuth: true }), json: payload })
      .json();
  }

  async function verifyPhoneNumberSend2FA(payload: VerifyPhoneNumberSend2FAPayload): Promise<
    | {
        isNewUser: false;
        phoneNumber: string;
        sentSMS: boolean;
        token: string;
        hasPowerTag: boolean;
        firstName: string;
        lastName: string;
        message: string;
        userId: string;
        isTwilioVerify: boolean;
      }
    | { isNewUser: true; phoneNumber: string; sentSMS: boolean; token: string; isTwilioVerify: boolean }
  > {
    return ky
      .post('phone/verify', { ...getCommonRequestOptions('auth', { useBasicAuth: true }), json: payload })
      .json();
  }

  async function verifyTwoFACode(payload: VerifyTwoFAPayload, authCodeToken: string): Promise<NewUser2FAVerifyRes> {
    return ky
      .post('2fa/verify', { ...getCommonRequestOptions('auth', { token: authCodeToken }), json: payload })
      .json();
  }

  async function verifyTwoFACodeLogin(payload: VerifyTwoFAPayload, authCodeToken: string): Promise<any> {
    return ky.post('login', { ...getCommonRequestOptions('auth', { token: authCodeToken }), json: payload }).json();
  }
  async function verifyTwoFACodeResetPin(payload: VerifyTwoFAPinResetPayload, authCodeToken: string): Promise<any> {
    return ky
      .post('2fa/reset-pin', { ...getCommonRequestOptions('auth', { token: authCodeToken }), json: payload })
      .json();
  }

  /** Use Twilio Verify Service to resend code, send code in a phone call, and send code in a whatsapp message. */
  async function sendTwilioVerify2FA(
    payload: TwilioVerifySend2FAPayload,
    authCodeToken: string // Require just to prevent users from triggering Verify Twilio. Must first try regular Twilio 2FA before trying Verify.
  ): Promise<{
    invalid: boolean;
    isNewUser: boolean;
    phoneNumber: string;
    sentSMS: boolean;
    token: string;
    hasPowerTag: boolean;
    firstName?: string;
    isTwilioVerify: boolean;
  }> {
    return ky
      .post('more-options/generate-2fa', {
        ...getCommonRequestOptions('auth', { token: authCodeToken }),
        json: payload,
      })
      .json();
  }

  /** Use Twilio Verify Service to verify code sent by SMS, call, or whatsapp. */
  // New user -> Verify only
  async function verifyTwilioVerify2FA(
    payload: VerifyTwoFAPayload,
    authCodeToken: string
  ): Promise<NewUser2FAVerifyRes> {
    return ky
      .post('more-options/verify-2fa', { ...getCommonRequestOptions('auth', { token: authCodeToken }), json: payload })
      .json();
  }

  // Existing user -> Verify + Log in
  async function verifyTwilioVerify2FALogin(payload: VerifyTwoFAPayload, authCodeToken: string): Promise<any> {
    return ky
      .post('more-options/verify-2fa-login', {
        ...getCommonRequestOptions('auth', { token: authCodeToken }),
        json: payload,
      })
      .json();
  }

  // Verify if the google user info received from one tap is a valid FLUZ user and can skip 2fa
  async function verifyOneTapUser(
    payload: VerifyOneTapUserPayload
  ): Promise<VerifyOneTapUserResponse | { skip2FA: false; success: boolean }> {
    return ky
      .post('google/verify', { ...getCommonRequestOptions('auth', { useBasicAuth: true }), json: payload })
      .json();
  }

  function handleRedirectOrIframeEvent({
    url,
    accessToken,
    refreshToken,
  }: {
    url: string;
    accessToken: string | string[];
    refreshToken: string | string[];
  }) {
    if (isIframe) {
      let parentOrigin: string | null = null;
      // Try to get parent origin using ancestorOrigins (Chrome/Safari)
      if (document.location.ancestorOrigins?.length) {
        parentOrigin = new URL(document.location.ancestorOrigins[document.location.ancestorOrigins.length - 1]).origin;
      } else {
        // Fallback for Firefox - use document.referrer
        parentOrigin = document.referrer ? new URL(document.referrer).origin : null;
      }

      if (!parentOrigin) {
        return;
      }

      const isTrustedOrigin = TRUSTED_ORIGINS.some((trusted) => {
        if (trusted === '*') return true;
        try {
          const regex = new RegExp(trusted);
          return regex.test(parentOrigin as string);
        } catch {
          return trusted === parentOrigin;
        }
      });

      if (isTrustedOrigin) {
        window.parent.postMessage({ accessToken, refreshToken, url }, parentOrigin);
      } else {
        console.error('Untrusted origin:', parentOrigin);
      }
    } else {
      window.location.assign(url);
    }
  }

  function handleUserRedirect(url: string, hasPowerTag: boolean) {
    const urlQueryParams = url?.substring(url?.indexOf('?') + 1);
    const { accessToken, refreshToken } = queryString.parse(urlQueryParams);
    if (!accessToken || !refreshToken) {
      throw new Error('No Access or Refresh Token received.');
    }

    if (['POWER', 'UNIFIED'].includes(redirectFrom)) {
      if (hasPowerTag) {
        handleRedirectOrIframeEvent({ url, accessToken, refreshToken });
      } else if (redirectFrom === 'UNIFIED') {
        // Current safety check for registration redirect. If existing user who has persistent redirect to UNIFIED creates a new account, need to redirect to call-to-action
        if (url[0] === '?') {
          return history.push('/login/call-to-action');
        }
        handleRedirectOrIframeEvent({ url, accessToken, refreshToken });
      } else {
        window.location.replace('https://fluz.app/business-account-application-form/');
      }
    } else if (redirectFrom === 'MOBILE' && isMobileOrTabletBrowser()) {
      window.location.assign(
        `fluz://?accessToken=${encodeURIComponent(accessToken as string)}&refreshToken=${encodeURIComponent(
          refreshToken as string
        )}`
      );
    } else {
      history.push('/login/call-to-action');
    }
  }

  return {
    verifySocialAuth,
    verifyEmailSend2FA,
    verifyPhoneNumberSend2FA,
    verifyTwoFACode,
    verifyTwoFACodeLogin,
    sendTwilioVerify2FA,
    verifyTwilioVerify2FA,
    verifyTwilioVerify2FALogin,
    verifyOneTapUser,
    handleUserRedirect,
    verifyTwoFACodeResetPin,
  };
}
